import { number, object, ref, string } from "yup";
import { tryParseFloat } from "../../shared";

export const DEFAULT_VALUES = {
  stepSph: 0.25,
};

// Check if SPH and CYL ranges overlap (descending order)
const isDescendingRangeOverlapping = (start1, end1, start2, end2) => {
  return start1 >= end2 && end1 <= start2;
};

// Check if ADD ranges overlap (ascending order)
const isAscendingRangeOverlapping = (start1, end1, start2, end2) => {
  return start1 <= end2 && end1 >= start2;
};

export const VALIDATION_SCHEMA = (
  hasAdd,
  existingGroups,
  canUpdateRetailPrice = true,
  canUpdateDistributorPrice = true
) =>
  object().shape({
    sphStart: string()
      .required("Start-nya Sph wajib diisi")
      .transform((value) => {
        const numericValue = parseFloat(value);
        if (numericValue === 0) return "0"; // Avoid converting 0 to 0.00
        return numericValue > 0
          ? `+${numericValue.toFixed(2)}`
          : numericValue.toFixed(2);
      }),

    sphEnd: string()
      .required("End-nya Sph wajib diisi")
      .transform((value) => {
        const numericValue = parseFloat(value);
        if (numericValue === 0) return "0"; // Avoid converting 0 to 0.00
        return numericValue > 0
          ? `+${numericValue.toFixed(2)}`
          : numericValue.toFixed(2);
      })
      .test(
        "sphEnd-lower-than-sphStart",
        "Start-nya Sph harus lebih kecil atau sama dengan End-nya Sph",
        function (value) {
          const sphStart = this.resolve(ref("sphStart"));
          return parseFloat(value) <= parseFloat(sphStart);
        }
      ),

    cylStart: string()
      .required("Start-nya Cyl wajib diisi")
      .transform((value) => {
        const numericValue = parseFloat(value);
        if (numericValue === 0) return "0"; // Avoid converting 0 to 0.00
        return numericValue > 0
          ? `+${numericValue.toFixed(2)}`
          : numericValue.toFixed(2);
      }),

    cylEnd: string()
      .required("End-nya Cyl wajib diisi")
      .transform((value) => {
        const numericValue = parseFloat(value);
        if (numericValue === 0) return "0"; // Avoid converting 0 to 0.00
        return numericValue > 0
          ? `+${numericValue.toFixed(2)}`
          : numericValue.toFixed(2);
      })
      .test(
        "cylEnd-lower-than-cylStart",
        "End-nya Cyl harus lebih kecil atau sama dengan Start-nya Cyl",
        function (value) {
          const cylStart = this.resolve(ref("cylStart"));
          return parseFloat(value) <= parseFloat(cylStart);
        }
      ),

    addStart: string()
      .nullable()
      .when([], {
        is: () => hasAdd,
        then: (schema) =>
          schema
            .required("Start-nya Add wajib diisi")
            .transform((value) => {
              const numericValue = parseInt(value);
              if (numericValue === 0) return "0"; // Avoid converting 0 to +0
              return `+${numericValue}`;
            })
            .test(
              "addStart-greater-than-zero",
              "Start-nya Add tidak boleh dibawah 0",
              (value) => parseInt(value) >= 0
            ),
      }),

    addEnd: string()
      .nullable()
      .when([], {
        is: () => hasAdd,
        then: (schema) =>
          schema
            .required("End-nya Add wajib diisi")
            .transform((value) => {
              const numericValue = parseInt(value);
              if (numericValue === 0) return "0"; // Avoid converting 0 to +0
              return `+${numericValue}`;
            })
            .test(
              "addEnd-greater-than-addStart",
              "End-nya Add harus lebih besar atau sama dengan Start-nya Add",
              function (value) {
                const addStart = this.resolve(ref("addStart"));
                return parseInt(value) >= parseInt(addStart);
              }
            ),
      }),

    overlapValidation: string().test(
      "no-overlap",
      "Range Sph, Cyl, dan Add (jika ada) tidak boleh tumpang tindih dengan grup varian yang sudah ada",
      function () {
        const { id, sphStart, sphEnd, cylStart, cylEnd, addStart, addEnd } =
          this.parent;
        const currentSphStart = parseFloat(sphStart);
        const currentSphEnd = parseFloat(sphEnd);
        const currentCylStart = parseFloat(cylStart);
        const currentCylEnd = parseFloat(cylEnd);
        const currentAddStart = tryParseFloat(addStart);
        const currentAddEnd = tryParseFloat(addEnd);

        return !existingGroups.some((group) => {
          if (group.id === id) return false; // Skip validation for itself

          const groupSphStart = parseFloat(group.sphStart);
          const groupSphEnd = parseFloat(group.sphEnd);
          const groupCylStart = parseFloat(group.cylStart);
          const groupCylEnd = parseFloat(group.cylEnd);
          const groupAddStart = tryParseFloat(group.addStart);
          const groupAddEnd = tryParseFloat(group.addEnd);

          const isSphOverlapping = isDescendingRangeOverlapping(
            groupSphStart,
            groupSphEnd,
            currentSphStart,
            currentSphEnd
          );
          const isCylOverlapping = isDescendingRangeOverlapping(
            groupCylStart,
            groupCylEnd,
            currentCylStart,
            currentCylEnd
          );

          if (currentAddStart === null || currentAddEnd === null) {
            // If ADD is not provided, only check SPH and CYL overlaps
            return isSphOverlapping && isCylOverlapping;
          }

          // If ADD is provided, check all three overlaps
          const isAddOverlapping = isAscendingRangeOverlapping(
            currentAddStart,
            currentAddEnd,
            groupAddStart,
            groupAddEnd
          );

          return isSphOverlapping && isCylOverlapping && isAddOverlapping;
        });
      }
    ),
    stepSph: number().required("Step Sph wajib diisi"),
    combination: number()
      .nullable()
      .transform((value) => value || null),
    retailPrice: number()
      .transform((value, originalValue) => {
        if (
          originalValue === "" ||
          originalValue === null ||
          originalValue === undefined ||
          isNaN(value)
        ) {
          return 0;
        }
        return value;
      })
      .when([], {
        is: () => canUpdateRetailPrice,
        then: (schema) => schema.required("Harga retail wajib diisi"),
        otherwise: (schema) => schema.nullable(),
      }),
    distributorPrice: number()
      .transform((value, originalValue) => {
        if (
          originalValue === "" ||
          originalValue === null ||
          originalValue === undefined ||
          isNaN(value)
        ) {
          return 0;
        }
        return value;
      })
      .when([], {
        is: () => canUpdateDistributorPrice,
        then: (schema) => schema.required("Harga distributor wajib diisi"),
        otherwise: (schema) => schema.nullable(),
      }),
  });
